import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, HeartHandshake, Stethoscope, BriefcaseBusiness, Check } from "lucide-react";
import { MaskedLines, FadeIn, EditorialMarquee, ChapterHeading } from "@/components/motion";
import { IMAGES } from "@/config/site";

const PATHWAYS = [
  {
    num: "01",
    icon: HeartHandshake,
    title: "Patients & Families",
    text: "Understand what the move from hospital to home can look like — and how to begin, calmly.",
    cta: "Explore care at home",
    to: "/patients-families",
    testid: "pathway-card-patients",
  },
  {
    num: "02",
    icon: Stethoscope,
    title: "Hospitals & Referral Partners",
    text: "A clear, dependable path for discharge planners, physicians, and care teams to refer patients.",
    cta: "Submit a referral",
    to: "/professionals",
    testid: "pathway-card-professionals",
  },
  {
    num: "03",
    icon: BriefcaseBusiness,
    title: "Careers at Homevia",
    text: "Join a team built around calm, careful transitions — for patients and for each other.",
    cta: "See careers",
    to: "/careers",
    testid: "pathway-card-careers",
  },
];

const CHAPTERS = [
  {
    num: "01",
    title: "The transition",
    text: "Leaving the hospital is a milestone, not an ending. We treat the days that follow as their own chapter of care — one that deserves as much attention as the stay itself.",
  },
  {
    num: "02",
    title: "The coordination",
    text: "Discharge instructions, follow-ups, equipment, questions at 9pm. We keep the moving parts connected so nothing — and no one — gets lost between settings.",
  },
  {
    num: "03",
    title: "The home",
    text: "Recovery happens best where people feel most themselves. Our work is shaped around the rhythms, routines, and rooms of each person's home.",
  },
  {
    num: "04",
    title: "The family",
    text: "Families carry transitions too. We make sure they understand the plan, know what to watch for, and never feel alone in the process.",
  },
];

const ASSURANCES = [
  "A calm, single point of coordination",
  "Clear communication at every step",
  "Plans shaped around the person and their family",
];

function HeroImage() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  return (
    <div ref={ref} className="relative">
      <div className="absolute -inset-5 -rotate-2 rounded-[2.5rem] bg-sage/25" aria-hidden />
      <motion.div
        style={{ y }}
        className="relative overflow-hidden rounded-[2rem] border border-olive/10 shadow-[0_40px_80px_-40px_rgba(43,51,43,0.45)]"
      >
        <img
          src={IMAGES.heroHands}
          alt="A smiling caregiver sitting with an elderly man in warm sunlight at home"
          data-testid="home-hero-image"
          className="h-[380px] w-full object-cover sm:h-[460px] lg:h-[560px]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-olive/35 via-transparent to-transparent" aria-hidden />
        <p className="absolute bottom-6 left-6 right-6 font-display italic text-lg text-cream/95">
          "Care doesn't end at the hospital door."
        </p>
      </motion.div>
    </div>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden px-6 pb-24 pt-16 lg:px-10 lg:pb-32 lg:pt-24">
        <div className="mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <FadeIn>
              <p
                data-testid="home-hero-eyebrow"
                className="text-sm font-bold uppercase tracking-[0.25em] text-teal"
              >
                Transitional care, centered on home
              </p>
            </FadeIn>
            <h1 className="mt-6 font-serif text-5xl font-medium leading-[1.02] tracking-tight text-ink sm:text-6xl lg:text-[5.25rem]">
              <MaskedLines
                lines={["Care that meets you", "at the front door."]}
                delay={0.15}
              />
            </h1>
            <FadeIn delay={0.6}>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-olive/75">
                Homevia supports patients and families through the move from hospital to home —
                with coordination, clarity, and calm. For care teams, we make every handoff a
                steady one.
              </p>
            </FadeIn>
            <FadeIn delay={0.75} className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/request-care"
                data-testid="home-hero-request-care-btn"
                className="inline-flex items-center gap-2 rounded-full bg-teal-deep px-7 py-3.5 text-sm font-bold text-cream transition-all duration-300 hover:bg-olive hover:shadow-xl"
              >
                Request Care <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/referral"
                data-testid="home-hero-referral-btn"
                className="inline-flex items-center gap-2 rounded-full border border-olive/20 px-7 py-3.5 text-sm font-bold text-olive transition-all duration-300 hover:border-teal-deep hover:bg-sage/10"
              >
                Submit a Referral <ArrowUpRight className="h-4 w-4" />
              </Link>
            </FadeIn>
          </div>
          <div className="lg:col-span-5">
            <FadeIn delay={0.35}>
              <HeroImage />
            </FadeIn>
          </div>
        </div>
      </section>

      <EditorialMarquee items={["Compassion", "Coordination", "Clarity", "Comfort", "Home"]} />

      {/* Pathways */}
      <section className="px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <FadeIn>
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-teal">Start where you are</p>
            <h2 className="mt-4 max-w-2xl font-serif text-4xl leading-tight tracking-tight text-ink lg:text-5xl">
              Three doors into Homevia
            </h2>
          </FadeIn>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {PATHWAYS.map((p, i) => (
              <FadeIn key={p.num} delay={i * 0.12}>
                <Link
                  to={p.to}
                  data-testid={p.testid}
                  className="group flex h-full flex-col rounded-3xl border border-olive/10 bg-white p-8 transition-all duration-500 hover:scale-[1.02] hover:border-teal/50 hover:shadow-[0_30px_60px_-30px_rgba(74,115,115,0.4)]"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-display italic text-lg text-teal">({p.num})</span>
                    <p.icon className="h-6 w-6 text-sage-dark transition-colors duration-300 group-hover:text-teal-deep" />
                  </div>
                  <h3 className="mt-8 font-serif text-2xl text-ink lg:text-[1.7rem]">{p.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-olive/70">{p.text}</p>
                  <span className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-teal-deep">
                    {p.cta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Manifesto chapters */}
      <section className="bg-sand/50 px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <FadeIn>
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-teal">Our care overview</p>
            <h2 className="mt-4 max-w-3xl font-serif text-4xl leading-tight tracking-tight text-ink lg:text-5xl">
              What transitional care means to us
            </h2>
          </FadeIn>
          <div className="mt-16 space-y-0">
            {CHAPTERS.map((c, i) => (
              <div
                key={c.num}
                className={`grid gap-6 border-t border-olive/10 py-12 lg:grid-cols-12 lg:gap-12 ${
                  i === CHAPTERS.length - 1 ? "border-b" : ""
                }`}
              >
                <div className="lg:col-span-4">
                  <ChapterHeading number={c.num} title={c.title} />
                </div>
                <FadeIn delay={0.15} className="lg:col-span-7 lg:col-start-6">
                  <p className="max-w-2xl text-lg leading-relaxed text-olive/75">{c.text}</p>
                </FadeIn>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <FadeIn>
              <div className="relative">
                <div className="absolute -inset-4 rotate-1 rounded-[2.5rem] bg-teal/10" aria-hidden />
                <div className="relative overflow-hidden rounded-[2rem] border border-olive/10">
                  <img
                    src={IMAGES.homeCare}
                    alt="A healthcare professional checking an elderly man's blood pressure at home"
                    data-testid="home-trust-image"
                    className="h-[380px] w-full object-cover lg:h-[480px]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-olive/25 via-transparent to-transparent" aria-hidden />
                </div>
              </div>
            </FadeIn>
          </div>
          <div className="lg:col-span-6">
            <FadeIn>
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-teal">Why Homevia</p>
              <h2 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-ink lg:text-5xl">
                Care you can feel confident in
              </h2>
            </FadeIn>
            <div className="mt-10 space-y-5">
              {ASSURANCES.map((a, i) => (
                <FadeIn key={a} delay={0.1 + i * 0.1} className="flex items-start gap-4">
                  <span className="mt-1 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-sage/25">
                    <Check className="h-4 w-4 text-teal-deep" />
                  </span>
                  <p className="text-lg text-olive/85">{a}</p>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.4} className="mt-10">
              <Link
                to="/consultation"
                data-testid="home-trust-consultation-btn"
                className="inline-flex items-center gap-2 rounded-full border border-olive/20 px-7 py-3.5 text-sm font-bold text-olive transition-all duration-300 hover:border-teal-deep hover:bg-sage/10"
              >
                Schedule a Consultation <ArrowRight className="h-4 w-4" />
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="px-6 pb-24 lg:px-10 lg:pb-32">
        <FadeIn className="mx-auto max-w-7xl">
          <div className="grain relative overflow-hidden rounded-[2.5rem] bg-olive px-8 py-16 text-center sm:px-16 lg:py-24">
            <div className="relative z-[2]">
              <p className="font-display italic text-xl text-sage">Take the first step</p>
              <h2 className="mx-auto mt-4 max-w-2xl font-serif text-4xl leading-tight text-cream lg:text-5xl">
                Not sure where to begin? Start with a conversation.
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-cream/70">
                A consultation is simply a chance to talk through your situation — no pressure, no
                obligation.
              </p>
              <div className="mt-10 flex flex-wrap justify-center gap-4">
                <Link
                  to="/consultation"
                  data-testid="home-cta-consultation-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-sage px-7 py-3.5 text-sm font-bold text-olive transition-all duration-300 hover:bg-cream hover:shadow-xl"
                >
                  Schedule a Consultation <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="home-cta-contact-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-7 py-3.5 text-sm font-bold text-cream transition-all duration-300 hover:bg-cream/10"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>
    </div>
  );
}
